import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSagawaUsage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const ALLOWED_EMAIL = 'daniel@quikbak.com';

const toInputDate = (date) => {
  const copy = new Date(date);
  const year = copy.getFullYear();
  const month = `${copy.getMonth() + 1}`.padStart(2, '0');
  const day = `${copy.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toSagawaDate = (value) => value.replace(/-/g, '');

export default function ShippingStatusPage() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    const today = new Date();
    const defaultEnd = toInputDate(today);
    const defaultStart = toInputDate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  }, []);

  const handleFetch = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getSagawaUsage({
        startDate: toSagawaDate(startDate),
        endDate: toSagawaDate(endDate),
      });
      const payload = response?.data || response || null;
      setUsage(payload);
      setDetails(payload?.details || []);
    } catch (err) {
      console.error('Failed to load Sagawa usage:', err);
      setError(err?.message || err?.details || t('shippingStatus.messages.error'));
      setUsage(null);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    if (!usage?.shippingDataList) {
      return { total: 0 };
    }
    const total = usage.shippingDataList.reduce((acc, item) => acc + (item?.outputShippingCount || 0), 0);
    return { total };
  }, [usage]);

  if (!currentUser || currentUser.email?.toLowerCase() !== ALLOWED_EMAIL) {
    return (
      <div className="container">
        <h1>{t('shippingStatus.title')}</h1>
        <div className="error-message">{t('shippingStatus.messages.noAccess')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>{t('shippingStatus.title')}</h1>
      <form className="shipping-status-filters" onSubmit={handleFetch}>
        <label>
          <span>{t('shippingStatus.fields.startDate')}</span>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(event) => setStartDate(event.target.value)}
            required
          />
        </label>
        <label>
          <span>{t('shippingStatus.fields.endDate')}</span>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(event) => setEndDate(event.target.value)}
            required
          />
        </label>
        <button type="submit" className="payment-submit-button" disabled={loading}>
          {loading ? t('shippingStatus.buttons.loading') : t('shippingStatus.buttons.refresh')}
        </button>
      </form>

      {error && (
        <div className="error-message" role="alert">
          {error}
          <button type="button" onClick={() => setError('')}>✕</button>
        </div>
      )}

      {usage && (
        <div className="shipping-status-summary">
          <div>
            <strong>{t('shippingStatus.summary.range')}</strong>: {usage.startDate} → {usage.endDate}
          </div>
          <div>
            <strong>{t('shippingStatus.summary.result')}</strong>: {usage.resultCode || t('shippingStatus.values.unknown')}
          </div>
          <div>
            <strong>{t('shippingStatus.summary.total')}</strong>: {summary.total}
          </div>
        </div>
      )}

      {usage?.shippingDataList?.length ? (
        <div className="shipping-status-table">
          <table>
            <thead>
              <tr>
                <th>{t('shippingStatus.table.deliveryCode')}</th>
                <th>{t('shippingStatus.table.count')}</th>
              </tr>
            </thead>
            <tbody>
              {usage.shippingDataList.map((row, index) => (
                <tr key={`${row.deliveryCode || 'row'}-${index}`}>
                  <td>{row.deliveryCode || t('shippingStatus.values.unknown')}</td>
                  <td>{row.outputShippingCount ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        usage && !loading && (
          <div className="info-message">{t('shippingStatus.messages.noResults')}</div>
        )
      )}

      {details.length > 0 && (
        <div className="shipping-status-details">
          <h2>{t('shippingStatus.details.title')}</h2>
          <div className="shipping-status-table">
            <table>
              <thead>
                <tr>
                  <th>{t('shippingStatus.details.matchId')}</th>
                  <th>{t('shippingStatus.details.hotel')}</th>
                  <th>{t('shippingStatus.details.shippedAt')}</th>
                  <th>{t('shippingStatus.details.tracking')}</th>
                  <th>{t('shippingStatus.details.printRequest')}</th>
                  <th>{t('shippingStatus.details.label')}</th>
                </tr>
              </thead>
              <tbody>
                {details.map((item) => {
                  const shippedAt = item.shippedAt ? new Date(item.shippedAt) : null;
                  return (
                    <tr key={item.matchId}>
                      <td>{item.matchId}</td>
                      <td>{item.hotelId || t('shippingStatus.values.unknown')}</td>
                      <td>{shippedAt ? shippedAt.toLocaleString() : t('shippingStatus.values.unknown')}</td>
                      <td>{item.shippingNumber || t('shippingStatus.values.unknown')}</td>
                      <td>{item.printRequestId || t('shippingStatus.values.unknown')}</td>
                      <td>
                        {item.labelUrl ? (
                          <a href={item.labelUrl} target="_blank" rel="noopener noreferrer">
                            {t('shippingStatus.details.download')}
                          </a>
                        ) : (
                          t('shippingStatus.values.unknown')
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {details.length === 0 && usage && !loading && summary.total > 0 && (
        <div className="info-message">{t('shippingStatus.messages.noDetails')}</div>
      )}
    </div>
  );
}
